import { stepPanelConfig } from '@/configs/step-panel.config';
import { ArrowLeftOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useInquiry } from '@hooks/inquiry.hook';
import useStep from '@hooks/step.hook';
import { navigate } from '@reach/router';
import { createEvent } from '@utils/calendar.utils';
// eslint-disable-next-line object-curly-newline
import { Button, message, Modal, Space } from 'antd';
import * as R from 'ramda';
import { useState, useRef } from 'react';
import { AZStepsFooter } from './styles';

export const phonePattern = /\(\d{3}\) \d{3}-\d{4}/;
// eslint-disable-next-line operator-linebreak
export const emailPattern =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const checkDisabled = {
  schedule: R.complement(R.path(['schedule', 'start'])),
  contact: R.pipe(
    R.prop('contact'),
    R.allPass([
      R.prop('first'),
      R.prop('last'),
      R.pipe(
        R.propOr('', 'phone'),
        R.ifElse(R.length, R.test(phonePattern), R.T),
      ),
      R.cond([
        [R.prop('noEmail'), R.T],
        [R.T, R.pipe(R.prop('email'), R.test(emailPattern))],
      ]),
    ]),
    R.not,
  ),
};

// Generate unique idempotency key for each submission attempt
const generateIdempotencyKey = () =>
  `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const StepsFooter = () => {
  const { stepIndex, step } = useStep();
  const [inquiry] = useInquiry();
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false); // Prevent double submissions
  const prevStep = stepPanelConfig?.[stepIndex - 1]?.key;
  const nextStep = stepPanelConfig?.[stepIndex + 1]?.key;
  const disabled = checkDisabled[step] && checkDisabled[step](inquiry);

  const submit = async () => {
    // Guard against double submission
    if (submittedRef.current || submitting) {
      return;
    }
    submittedRef.current = true;
    setSubmitting(true);

    try {
      const idempotencyKey = generateIdempotencyKey();
      await createEvent({ ...inquiry, idempotencyKey });
      message.success(
        'Application submitted successfully. Check your email for confirmation.',
        4,
      );
      navigate('/success');
      return;
    } catch (error) {
      // Reset on error to allow retry
      submittedRef.current = false;
      setSubmitting(false);
      throw error;
    }
  };

  const confirm = () => {
    if (submitting) return; // Prevent opening modal while submitting

    Modal.confirm({
      icon: <ExclamationCircleFilled />,
      title: "Are you sure you're ready to submit?",
      content:
        "Please double check that your information is correct. After submitting, you won't be able to change your appointment.",
      okText: 'Submit',
      cancelText: 'Cancel',
      cancelButtonProps: {
        type: 'link',
      },
      onOk: submit,
    });
  };

  return (
    <AZStepsFooter justifyContent={['center', 'flex-end']} pr={[6]} py={[1, 3]}>
      <Space>
        <Button
          icon={<ArrowLeftOutlined />}
          type="link"
          size="large"
          disabled={stepIndex === 0}
          onClick={() => {
            navigate(`/app/${prevStep}`);
          }}
        >
          Previous Step
        </Button>
        <Button
          type="primary"
          size="large"
          disabled={disabled || submitting}
          loading={submitting}
          onClick={() => {
            if (nextStep) {
              navigate(`/app/${nextStep}`);
            } else {
              confirm();
            }
          }}
        >
          {step === 'contact' ? 'Submit' : 'Next Step'}
        </Button>
      </Space>
    </AZStepsFooter>
  );
};

export default StepsFooter;
